import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  pageTitle?: string;
  showTermsLinks?: boolean;
  showInvitationLink?: boolean;
  showCalLink?: boolean;
}

const AuthLayout = ({
  children,
  title,
  pageTitle,
  showTermsLinks = true,
  showInvitationLink = false,
  showCalLink = false,
}: AuthLayoutProps) => {
  return (
    <>
      <Helmet>
        <title>{pageTitle || title} | Hobbes</title>
      </Helmet>

      {/* Full-screen dark background with blurred image */}
      <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#0f0e0d]">
        {/* Background image with blur */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://storage.googleapis.com/hobbes-assets/HobbesPlatform_BackgroundImage.png)',
            filter: 'blur(10px)',
            transform: 'scale(1.1)', // Prevent blur edge artifacts
          }}
        />

        {/* Semi-transparent overlay */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(15,14,13,0.5)' }}
        />

        {/* Centered card container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 w-[567px] min-h-[634px] mx-4"
        >
          <div
            className="rounded-[28px] p-6 h-full flex flex-col"
            style={{
              backgroundColor: 'rgba(23,23,23,0.8)',
              border: '1px solid #464646',
              backdropFilter: 'blur(20px)',
            }}
          >
            {/* Inner content wrapper - 364px centered */}
            <div className="w-[364px] mx-auto flex flex-col flex-1 pt-4">
              {/* Hobbes Logo */}
              <div className="flex justify-center mb-5">
                <img
                  src="https://storage.googleapis.com/hobbes-assets/FullLogo2026.svg"
                  alt="Hobbes"
                  className="h-7 object-contain"
                />
              </div>

              {/* Title */}
              <h1 className="text-2xl font-medium text-white text-center mb-6 tracking-[-0.24px]">
                {title}
              </h1>

              {/* Form content */}
              <div className="flex-1">
                {children}
              </div>

              {/* Invitation link */}
              {showInvitationLink && (
                <div className="text-center text-sm mt-6 text-[#a4a4a4] tracking-[-0.14px]">
                  Have an invitation? Open your link to sign up.
                </div>
              )}

              {/* Cal.com link */}
              {showCalLink && (
                <div className="text-center text-sm mt-4 text-[#a4a4a4] tracking-[-0.14px]">
                  No invitation?{' '}
                  <a
                    href="https://cal.com/team/hobbes/hobbes-intro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white underline underline-offset-4 hover:text-white/80 transition-colors"
                  >
                    Meet the Hobbes team
                  </a>
                </div>
              )}

              {/* Terms and Privacy links */}
              {showTermsLinks && (
                <div className="text-center text-xs mt-auto pt-6 tracking-[-0.12px] text-[#a4a4a4]">
                  By continuing, you agree to{' '}
                  <a
                    href="https://www.hihobbes.com/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-white/80 transition-colors"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="https://www.hihobbes.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-white/80 transition-colors"
                  >
                    Privacy Policy
                  </a>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default AuthLayout;
